import { createDraftSafeSelector } from '@reduxjs/toolkit'

import { formatDate } from 'src/Constants/formatDate'
import { RootState } from 'src/Redux'
import { SliceStatus } from 'src/Redux/types'
import dayJs from 'src/Helpers/dayjs'
import { APP } from 'src/Configs/App'
import { CampaignTypeEnum } from 'src/Types/campaigns'

import { UserState } from '.'

const getState = (state: RootState): UserState => state.authentication.user

export const getUser = createDraftSafeSelector(getState, ({ user }) => user)

export const getHasPassword = createDraftSafeSelector(
  getState,
  ({ hasPassword }) => hasPassword,
)

export const getUserEmail = createDraftSafeSelector(
  getUser,
  ({ email }) => email,
)

export const getUserCreatedAt = createDraftSafeSelector(
  getUser,
  ({ createdAt }) => createdAt,
)

export const getUserPhotoUrl = createDraftSafeSelector(
  getUser,
  ({ user_photo_url }) => user_photo_url,
)

export const getUserIntercomHash = createDraftSafeSelector(
  getUser,
  ({ intercomHash }) => intercomHash,
)

export const getIsNewUserForRemoveLowReachExperiment = createDraftSafeSelector(
  getState,
  ({ user, status }) =>
    dayJs(user.createdAt).isAfter(dayJs(APP.DATE_IS_NEW_ARTIST_LOW_REACH)) &&
    status === SliceStatus.Succeeded,
)

export const getIsNewUserForBudgetRangeExperiment = createDraftSafeSelector(
  getState,
  ({ user, status }) =>
    dayJs(user.createdAt).isAfter(
      dayJs(APP.DATE_IS_NEW_BUDGET_RANGE_EXPERIMENT),
    ) && status === SliceStatus.Succeeded,
)

export const getIsNewUserForTikTokMinBudget = createDraftSafeSelector(
  getState,
  ({ user, status }) =>
    dayJs(user.createdAt).isAfter(
      dayJs(APP.DATE_IS_NEW_USER_TIKTOK_MIN_BUDGET_100_150_EXP),
    ) && status === SliceStatus.Succeeded,
)

export const getIsNewUserForSpotifyMinBudget = createDraftSafeSelector(
  getState,
  ({ user, status }) =>
    dayJs(user.createdAt).isAfter(
      dayJs(APP.DATE_IS_NEW_USER_SPOTIFY_MIN_BUDGET_60_80_EXP),
    ) && status === SliceStatus.Succeeded,
)

export const getIsNewUserForManualMatchingExperiment = createDraftSafeSelector(
  getState,
  ({ user, status }) =>
    dayJs(user.createdAt).isAfter(
      dayJs(APP.DATE_IS_NEW_USER_MANUAL_MATCHING_EXP),
    ) && status === SliceStatus.Succeeded,
)

export const getIsNewUserForManualMatchingV2Experiment =
  createDraftSafeSelector(
    getState,
    ({ user, status }) =>
      dayJs(user.createdAt).isAfter(
        dayJs(APP.DATE_IS_NEW_USER_MANUAL_MATCHING_V2_EXP),
      ) && status === SliceStatus.Succeeded,
  )

export const getIsNewUserForThresholdCouponExperiment = createDraftSafeSelector(
  getState,
  ({ user, status }) =>
    dayJs(user.createdAt).isAfter(
      dayJs(APP.DATE_IS_NEW_USER_THRESHOLD_COUPON_EXP),
    ) && status === SliceStatus.Succeeded,
)

export const getIsNewUserForVideosBased = createDraftSafeSelector(
  getState,
  ({ user, status }) =>
    dayJs(user.createdAt).isAfter(
      dayJs(APP.DATE_IS_NEW_USER_VIDEOS_BASED).format(formatDate.formatDateISO),
    ) && status === SliceStatus.Succeeded,
)

export const getIsOldUserForRemoveLowReachExperiment = createDraftSafeSelector(
  getState,
  ({ user, status }) =>
    dayJs(user.createdAt).isBefore(dayJs(APP.DATE_IS_NEW_ARTIST_LOW_REACH)) &&
    status === SliceStatus.Succeeded,
)

export const getIsNewUserForBonusSystem = createDraftSafeSelector(
  getState,
  ({ user, getUserStatus }) =>
    dayJs(user.createdAt).isAfter(
      dayJs(APP.DATE_IS_NEW_CURATOR_BONUS_SYSTEM),
    ) && getUserStatus === SliceStatus.Succeeded,
)

export const getIsOldUserForBonusSystem = createDraftSafeSelector(
  getState,
  ({ user, getUserStatus }) =>
    dayJs(user.createdAt).isBefore(
      dayJs(APP.DATE_IS_NEW_CURATOR_BONUS_SYSTEM),
    ) && getUserStatus === SliceStatus.Succeeded,
)

const DATE_IS_NEW_USER_NEW_UI = '2024-12-05'

export const getIsNewUserForNewUI = createDraftSafeSelector(
  getState,
  ({ user }) => dayJs(user.createdAt).isAfter(dayJs(DATE_IS_NEW_USER_NEW_UI)),
)

export const getIsUserLoading = createDraftSafeSelector(
  getState,
  ({ status }) => status === SliceStatus.Loading,
)

export const getUserPhone = createDraftSafeSelector(
  getState,
  ({ phone_number }) => phone_number,
)
export const getIsEmailBounced = createDraftSafeSelector(
  getState,
  ({ isEmailBounced }) => isEmailBounced,
)

export const getIsEmailConfirmed = createDraftSafeSelector(
  getState,
  ({ user }) => user.is_email_confirmed,
)

export const getVerified = createDraftSafeSelector(
  getState,
  ({ verified }) => verified,
)

export const getCountryName = createDraftSafeSelector(
  getState,
  ({ user }) => user.country,
)

export const getCuratorSpotifyId = createDraftSafeSelector(
  getState,
  ({ user }) => user.spotifyId,
)

export const getUserSelector = createDraftSafeSelector(
  getState,
  ({ user }) => user,
)

export const getIsFirstCampaign = createDraftSafeSelector(
  getState,
  ({ user }) => user?.is_first_campaign,
)

export const getUserId = createDraftSafeSelector(getState, ({ user }) =>
  user?.id ? user.id : null,
)

export const getInfluencerId = createDraftSafeSelector(
  getState,
  ({ user }) => user?.influencerId,
)

export const getAreTermsAccepted = createDraftSafeSelector(
  getState,
  ({ user }) => user.are_terms_accepted,
)

export const getTermsPosition = createDraftSafeSelector(
  getState,
  ({ user }) => user.terms_position || 1,
)

export const getIsNewTerm = createDraftSafeSelector(
  getState,
  ({ user }) => user.is_new_term,
)

export const getArtistBalance = createDraftSafeSelector(
  getUserSelector,
  ({ balance }) => Number(balance),
)

export const getReferralCode = createDraftSafeSelector(
  getState,
  ({ referralCode }) => referralCode,
)

export const getIsBlockedCreatorSelector = createDraftSafeSelector(
  getState,
  ({ user }) => user.isBlocked,
)

export const getUserCampaignTypeSelector = createDraftSafeSelector(
  getState,
  ({ user }) =>
    user.is_first_campaign ? CampaignTypeEnum.FIRST : CampaignTypeEnum.BASIC,
)

export const getUserFirstNameSelector = createDraftSafeSelector(
  getState,
  ({ user }) => user.first_name,
)

export const getUserLastNameSelector = createDraftSafeSelector(
  getState,
  ({ user }) => user.last_name,
)

export const getUserRoleSelector = createDraftSafeSelector(
  getState,
  ({ user }) => user.role,
)
export const getIsUserReferral = createDraftSafeSelector(
  getState,
  ({ user }) => user.isReferral,
)

export const getUserFollowersCount = createDraftSafeSelector(
  getState,
  ({ user }) => user.followerCount,
)

export const getArtistRoleSelector = createDraftSafeSelector(
  getState,
  ({ user }) => user.artist?.artist_role,
)
